import { clearSession, getToken } from "../auth/session"

/** Всё ходит на свой origin: локально его даёт vite-proxy, на проде — nginx.
 *  Абсолютных адресов API в бандле быть не должно — это болячка старого фронта. */

export class ApiError extends Error {
	constructor(
		readonly status: number,
		message: string
	) {
		super(message)
	}
}

type Query = Record<string, string | number | boolean | null | undefined>

function toQueryString(params: Query): string {
	const search = new URLSearchParams()
	for (const [name, value] of Object.entries(params)) {
		if (value === null || value === undefined) continue
		search.append(name, String(value))
	}
	const text = search.toString()
	return text ? `?${text}` : ""
}

async function request<T>(
	path: string,
	init: RequestInit,
	authorized = true
): Promise<T> {
	const headers = new Headers(init.headers)
	if (authorized) {
		const token = getToken()
		if (token) headers.set("Authorization", `Bearer ${token}`)
	}

	const response = await fetch(path, { ...init, headers })

	if (response.status === 401) {
		clearSession()
		if (window.location.pathname !== "/login") window.location.assign("/login")
		throw new ApiError(401, "Сессия истекла — нужно войти заново")
	}

	if (!response.ok) {
		const text = await response.text().catch(() => "")
		throw new ApiError(response.status, text || `HTTP ${response.status}`)
	}

	if (response.status === 204) return undefined as T

	const raw = await response.text()
	if (!raw) return undefined as T

	try {
		return JSON.parse(raw) as T
	} catch {
		// html здесь — не данные, а чужая страница (SPA-fallback vite на
		// непроксированный `/ai/*` без `RIGA_AI_ORIGIN`, 502/504-заглушка прокси
		// и т.п.) — тот же признак, что уже используют `describeError`/
		// `describeSiteError` ниже. Не бросать здесь означало бы тихо положить
		// эту страницу в кэш react-query как будто это T — вызывающий код
		// (например, `rows.map`) падает потом без единой подсказки, откуда
		// растут ноги (см. находку с белым экраном на «Каталоги и сайты»,
		// 26 августа). auth отдаёт JWT голой строкой, не JSON, но JWT не
		// начинается с `<` — этот случай регулярка не задевает.
		if (/^\s*</.test(raw)) throw new ApiError(response.status, raw)
		return raw as unknown as T
	}
}

function withBody(method: string) {
	return <T>(path: string, body: unknown, authorized = true) =>
		request<T>(
			path,
			{
				method,
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			},
			authorized
		)
}

export const api = {
	get: <T>(path: string, params: Query = {}, authorized = true) =>
		request<T>(path + toQueryString(params), { method: "GET" }, authorized),

	post: withBody("POST"),
	put: withBody("PUT"),

	delete: <T>(path: string, params: Query = {}, authorized = true) =>
		request<T>(path + toQueryString(params), { method: "DELETE" }, authorized),
}

/** Картинки лежат за /static-files: локально vite-proxy тянет их с прода,
 *  на рижском сервере их отдаёт nginx из своего кэша.
 *
 * `encodeURI` — пути на статике содержат настоящие пробелы и скобки
 * (`Photo Group (TIFF, GIF, PNG)/591101.gif`); браузер их прощает, а
 * `fetch` в Node (react-pdf при рендере PDF, `scripts/smoke-render.tsx`) —
 * нет, падает `fetch failed`. Кодируем централизованно здесь, а не по
 * вызовам — раньше это делали только `pictogramUrl`/`attributeIconUrl`/
 * `seriesImageUrl` сами, для обычных фото товара кодирования не было вовсе.
 *
 * `RIGA_TEST_STATIC_ORIGIN` — только для `scripts/smoke-render.tsx`: в чистом
 * Node (без браузера и без vite-proxy) относительный `/static-files/...`
 * резолвится как файловый путь, не HTTP-адрес (react-pdf/image в Node не
 * подставляет origin сам, см. заголовок `vite.config.ts` про тот же прод-адрес
 * по умолчанию). В самом приложении переменная не задаётся — поведение то же,
 * что было. */
export function staticFileUrl(imageUrl: string): string {
	const path = encodeURI(imageUrl.replace(/^\/+/, ""))
	const testOrigin = typeof process !== "undefined" ? process.env?.RIGA_TEST_STATIC_ORIGIN : undefined
	return testOrigin ? `${testOrigin}/static-files/${path}` : `/static-files/${path}`
}

/** Бренд печатного каталога — определяет и визуальный стиль PDF
 *  (`screens/pdf/style.ts`), и папку статики для пиктограмм/атрибутов/серий.
 *  Соответствует `CatalogPdfStyle` в `Product.Domain.ERP.Common...Catalog.cs`
 *  (`Avtodelo = 0, Beril = 1`) — `Catalog.catalogPdfStyle` в `api/types.ts`. */
export type CatalogBrand = "Avtodelo" | "Beril"

export function brandFromCatalogPdfStyle(catalogPdfStyle: number): CatalogBrand {
	return catalogPdfStyle === 1 ? "Beril" : "Avtodelo"
}

/**
 * Пиктограммы лежат в своей папке статики, а API отдаёт одно имя файла.
 * Путь повторяет старый фронт (`CatalogPdfStyle.routes.prictogram`); пробелы и
 * скобки в нём настоящие — кодирует уже сам `staticFileUrl`.
 *
 * РФ различает печатный/веб вариант через `convertImageUrl`/`imageUrl` по
 * `TypePdfView`; Рига принципиально веб-качество (см. `CatalogDocument.tsx`),
 * что для РФ-роутера пиктограмм и есть `convertImageUrl ?? imageUrl` —
 * необязательный `convertImageUrl` можно не передавать там, где его нет
 * (`PictogramsPanel.tsx` зовёт без него, как и раньше).
 */
export function pictogramUrl(
	imageUrl: string,
	convertImageUrl?: string | null,
	brand: CatalogBrand = "Avtodelo"
): string {
	const folder = `Volume/DesignMaterials/Source Photo Materials/Pictograms (PDF, PNG, SVG)/${brand}`
	return staticFileUrl(`${folder}/${convertImageUrl || imageUrl}`)
}

/** Полоска-логотип серии товара под фотографией группы. */
export function seriesImageUrl(convertImageUrl: string, brand: CatalogBrand = "Avtodelo"): string {
	const folder = `Volume/DesignMaterials/Source Photo Materials/Series (PDF, SVG, PNG)/${brand}`
	return staticFileUrl(`${folder}/${convertImageUrl}`)
}

/**
 * Иконка-замена заголовка колонки в таблице товаров PDF-каталога, для двух
 * атрибутов из `ATTRIBUTE_REPLACE_WITH_ICON` (`screens/pdf/constants.ts`) —
 * порт `CatalogPdfStyle.routes.attribute` (РФ). Всегда SVG на статике; в
 * отличие от `@react-pdf/renderer` (не умел декодировать SVG через
 * `<Image src>`, поэтому РФ-иконки были закоммичены как векторные
 * PDF-компоненты) настоящий DOM-`<img>` открывает SVG сам — свой векторный
 * компонент на каждый атрибут/бренд не нужен.
 */
export function attributeIconUrl(attributeId: number, brand: CatalogBrand = "Avtodelo"): string {
	const folder = `Volume/DesignMaterials/Source Photo Materials/Attribute Pictograms (PDF, SVG)/${brand}`
	return staticFileUrl(`${folder}/${attributeId}.svg`)
}

/**
 * Текст ошибки для человека. Единственное место, где ответ сервера
 * превращается в строку на экране.
 *
 * Источников два, и отвечают они по-разному. `ecm` отдаёт текст ошибки голой
 * строкой (`ExceptionHandler.cs`), иногда в кавычках. Рижский BFF отвечает
 * `ProblemDetails` — json, у которого смысл лежит в `detail`. `502` и `504`
 * приходят не от них, а от прокси — с html внутри, который показывать
 * бессмысленно.
 */
export function describeError(error: unknown): string {
	if (!(error instanceof ApiError)) {
		return error instanceof Error ? error.message : String(error)
	}

	const text = problemDetail(error.message) ?? error.message.trim().replace(/^"|"$/g, "")

	if (error.status === 502 || error.status === 504 || /^\s*</.test(text)) {
		return `Сервер не ответил (${error.status}). Повторите запрос — если не помогает, ecm перезапускается.`
	}
	return text || `Ошибка ${error.status}`
}

/**
 * То же, но для операций в базе самого сайта — их ведёт рижский BFF
 * (`/ai/site/*`), и `502` у него значит своё.
 *
 * Сам BFF от недоступной базы не падает: он ловит `MySqlException` и отвечает
 * `502` с текстом. Пустой ответ прокси на том же коде — это уже другое: лежит
 * контейнер `riga-bff`.
 */
export function describeSiteError(error: unknown): string {
	if (!(error instanceof ApiError)) return describeError(error)

	const detail = problemDetail(error.message)
	if (detail) return detail

	if (error.status === 502 || error.status === 504 || /^\s*</.test(error.message)) {
		return `Рижский BFF не ответил (${error.status}) — база сайта тут ни при чём, смотреть надо контейнер riga-bff на узле.`
	}
	return describeError(error)
}

/** `detail` из `ProblemDetails`, если ответ им и был. */
function problemDetail(body: string): string | null {
	const text = body.trim()
	if (!text.startsWith("{")) return null
	try {
		const parsed = JSON.parse(text) as { detail?: string; title?: string }
		return parsed.detail ?? parsed.title ?? null
	} catch {
		return null
	}
}
